/**
 * WholesaleToggle — the on/off switch for counting the wholesale (B2B) channel
 * in the numbers. Lives in the "Revenue adjustments" group of the filter
 * sidebar (variant="row"), alongside the other revenue toggles, so all the
 * "what counts as revenue" switches sit together (founder 2026-07-03). Hidden
 * entirely unless the account actually has a wholesale bucket (most don't). Off
 * by default; flipping it re-runs every number with wholesale folded in or out.
 */

import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "./api-context.js";
import { invalidateFilterQueries, relockFinancialQueries } from "./query-invalidation.js";
import { CheckRow } from "../components/DrillExplorer.js";

interface SettingsResponse {
  settings: { includeWholesale?: boolean } | null;
  hasWholesale: boolean;
}

export function WholesaleToggle({ variant = "pill" }: { variant?: "pill" | "row" }): React.ReactElement | null {
  const api = useApiClient();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["catalog-settings"],
    queryFn: () => api.get<SettingsResponse>("/catalog/settings"),
  });

  const include = data?.settings?.includeWholesale ?? false;

  const toggle = async (next: boolean): Promise<void> => {
    // Optimistic so the checkbox flips instantly; the invalidate below refetches
    // the real value + every number with wholesale now folded in/out.
    qc.setQueryData<SettingsResponse>(["catalog-settings"], (old) =>
      old ? { ...old, settings: { ...(old.settings ?? {}), includeWholesale: next } } : old,
    );
    try {
      await api.post("/catalog/wholesale-include", { include: next });
      relockFinancialQueries(qc);
    } finally {
      // Only the DATA numbers need to re-run with wholesale folded in/out — NOT
      // the shell/gate queries (refetching those blanks the page, founder 2026-07-06).
      await invalidateFilterQueries(qc);
    }
  };

  if (variant === "row") {
    if (!data) return null; // still loading
    // Visibly disabled with a reason, never silently hidden (founder rule) —
    // he went looking for this switch and it wasn't anywhere.
    if (!data.hasWholesale) {
      return (
        <div style={{ opacity: 0.55, pointerEvents: "none" }} aria-disabled="true">
          <CheckRow label="Include wholesale" sub="no wholesale (B2B) channel in your catalog yet" on={false} onClick={() => undefined} />
        </div>
      );
    }
    return <CheckRow label="Include wholesale" sub="count your B2B channel in these numbers" on={include} onClick={() => void toggle(!include)} />;
  }

  if (!data?.hasWholesale) return null;

  return (
    <label
      title="Count your wholesale (B2B) channel in these numbers"
      style={{ display: "inline-flex", alignItems: "center", gap: 7, cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: "var(--body)", border: "1px solid var(--border)", borderRadius: 999, padding: "5px 11px", background: "var(--surface)", whiteSpace: "nowrap" }}
    >
      <input type="checkbox" checked={include} onChange={(e) => void toggle(e.target.checked)} style={{ width: 14, height: 14, cursor: "pointer" }} aria-label="Include wholesale in numbers" />
      Include wholesale
    </label>
  );
}
